import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchArticles, migrateNewsToSupabase, type NewsArticle } from '@/utils/adminStore';
import ScrollReveal from '@/components/base/ScrollReveal';

const PAGE_SIZE = 10;

interface Props {
  activeCategory: string;
}

export default function NewsList({ activeCategory }: Props) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      await migrateNewsToSupabase();
      const data = await fetchArticles();
      setArticles(data);
    } catch {
      setLoadError('加载资讯数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const result = activeCategory === 'all'
      ? articles
      : articles.filter((a) => a.category === activeCategory);
    return result;
  }, [articles, activeCategory]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const showPagination = filtered.length > PAGE_SIZE;

  const paginatedArticles = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold mb-3" />
        <p className="text-sm text-gray-400">正在加载资讯...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-sm text-gray-500 mb-4">{loadError}</p>
        <button
          onClick={loadData}
          className="inline-flex items-center gap-2 px-7 py-3 bg-brand-light text-brand-dark text-sm font-medium rounded-full hover:bg-brand-cream transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          重试
        </button>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-white text-brand-brown">
          <i className="ri-file-search-line text-2xl" />
        </div>
        <p className="text-gray-400 text-sm">暂无相关文章</p>
      </div>
    );
  }

  const displayArticles = paginatedArticles;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {displayArticles.map((article, index) => {
          return (
            <ScrollReveal key={article.id} variant="fade-up" delay={index * 80}>
              <article
                onClick={() => navigate(`/news/${article.id}`)}
                className="group bg-white rounded-2xl overflow-hidden border border-brand-light/30 hover:border-brand-cream hover:shadow-md transition-all duration-300 cursor-pointer h-full flex flex-col"
              >
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="text-[10px] font-medium text-brand-deep bg-white/85 backdrop-blur-sm px-2.5 py-1 rounded-full">
                      {article.category === 'brand' ? '品牌新闻' :
                       article.category === 'training' ? '培训赋能' :
                       article.category === 'store' ? '门店动态' :
                       article.category === 'franchise' ? '招商资讯' : '健康知识'}
                    </span>
                  </div>
                </div>

                <div className="p-5 md:p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-calendar-line text-xs" />
                      {article.date}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-brand-deep mb-3 leading-snug group-hover:text-brand-brown transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 flex-1">
                    {article.summary}
                  </p>
                  <div className="mt-4 pt-4 border-t border-brand-light/20 flex items-center justify-between">
                    <span className="text-xs text-brand-brown font-medium flex items-center gap-1 group-hover:text-brand-deep transition-colors">
                      阅读全文
                      <i className="ri-arrow-right-line text-xs" />
                    </span>
                  </div>
                </div>
              </article>
            </ScrollReveal>
          );
        })}
      </div>

      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-10 pt-6 border-t border-brand-light/30">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-white hover:text-brand-deep disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                currentPage === page
                  ? 'bg-brand-deep text-white'
                  : 'text-gray-500 hover:bg-white hover:text-brand-deep'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-white hover:text-brand-deep disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      )}
    </div>
  );
}